import { useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import styled from 'styled-components';
import { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import { anchoredStampSizePt } from '@dealstack/shared/lib/common/business/stampGeometry';
import { ptToMm } from '@dealstack/shared/lib/common/documentLayout/documentLayoutConstants';
import { PALETTE } from '@dealstack/shared/lib/common/business/stampDesign';
import { StampPreview } from '@dealstack/frontend/shared/components/StampPreview/StampPreview.tsx';

import type { DateNodeOptions } from './dateNode';
import { geometryAttributes, movedGeometry, resizedGeometry } from './signatureGeometry';
import type { SignatureGeometry } from './signatureGeometry';
import { anchorInsertPoint, relocateAnchor } from './signatureDrop';
import { setAnchorDropHint } from './signaturePairHighlight';
import { autoScroll, scrollableAncestor } from './dragScroll';
import { DATE_NODE_NAME } from './dateNode';

/** Vertical travel before a drag becomes a move. */
const RELOCATE_THRESHOLD_PX = 10;

interface Draft {
	geometry: SignatureGeometry;
	liftYPx: number;
}

/**
 * A date anchor, drawn as the box the effective date is written into.
 * Owns a full-width band so nothing wraps beside it; unlike a signature it never pairs.
 */
export const createDateNodeView =
	(options: DateNodeOptions) =>
	({ node, selected, updateAttributes, editor, getPos }: NodeViewProps) => {
		const showMetadata = options.showStampMetadata ?? true;

		// Same sizer `flowRender` reserves date space with, so the anchor matches the PDF.
		const designPt = anchoredStampSizePt(showMetadata, false);
		const designWidthMm = ptToMm(designPt.width);
		const ratio = designPt.width / designPt.height;

		const bandRef = useRef<HTMLDivElement>(null);
		const boxRef = useRef<HTMLDivElement>(null);
		/** Committed on release, so a drag is one edit rather than one per frame. */
		const [draft, setDraft] = useState<Draft | null>(null);

		const storedWidthMm = typeof node.attrs.widthMm === 'number' ? node.attrs.widthMm : null;
		const widthMm = draft?.geometry.widthMm ?? storedWidthMm ?? designWidthMm;
		const offsetXMm =
			draft?.geometry.offsetXMm ??
			(typeof node.attrs.offsetXMm === 'number' ? node.attrs.offsetXMm : 0);
		const heightMm = widthMm / ratio;
		const placementId = String(node.attrs.templateDatePlacementId ?? '');

		const selectSelf = (): void => {
			const pos = getPos();
			if (typeof pos === 'number') {
				editor.commands.setNodeSelection(pos);
			}
		};

		/**
		 * Where the box would land if released now, or null to leave it in place. Checked by
		 * building the transaction, so the drop line never promises a refused drop.
		 */
		const dropPosAt = (clientX: number, clientY: number): number | null => {
			const pos = getPos();
			if (typeof pos !== 'number') {
				return null;
			}
			const { view } = editor;
			const coords = view.posAtCoords({ left: clientX, top: clientY });
			if (coords === null) {
				return null;
			}
			// Snapped to a block position so the drop line shows where the box will really land.
			const landing = anchorInsertPoint(
				view.state.doc,
				view.state.schema,
				coords.pos,
				DATE_NODE_NAME
			);
			if (landing === null) {
				return null;
			}
			const source = { from: pos, to: pos + node.nodeSize };
			return relocateAnchor(view.state, source, landing, node) === null ? null : landing;
		};

		const beginGesture = (
			event: ReactPointerEvent<HTMLElement>,
			gesture: 'box' | 'resize'
		): void => {
			const band = bandRef.current;
			const box = boxRef.current;
			if (!editor.isEditable || band === null || box === null || event.button !== 0) {
				return;
			}
			const boxWidthPx = box.getBoundingClientRect().width;
			if (boxWidthPx === 0) {
				return;
			}
			// From the box's known mm width, so any transform or zoom is already in the ratio.
			const mmPerPx = widthMm / boxWidthPx;
			const bandWidthMm = band.getBoundingClientRect().width * mmPerPx;
			const startX = event.clientX;
			const startY = event.clientY;
			const start: SignatureGeometry = { widthMm, offsetXMm };
			const scroller = scrollableAncestor(box);

			event.preventDefault();
			event.stopPropagation();
			selectSelf();
			event.currentTarget.setPointerCapture(event.pointerId);

			/** Sticky, so recrossing the threshold does not flip back to sliding. */
			let relocating = false;
			let landing: number | null = null;

			const geometryAt = (clientX: number): SignatureGeometry => {
				const deltaMm = (clientX - startX) * mmPerPx;
				return gesture === 'resize'
					? resizedGeometry(start, deltaMm, bandWidthMm)
					: movedGeometry(start, deltaMm, bandWidthMm);
			};

			const onMove = (moveEvent: PointerEvent): void => {
				const geometry = geometryAt(moveEvent.clientX);
				if (gesture === 'resize') {
					setDraft({ geometry, liftYPx: 0 });
					return;
				}
				relocating =
					relocating || Math.abs(moveEvent.clientY - startY) > RELOCATE_THRESHOLD_PX;
				if (!relocating) {
					setDraft({ geometry, liftYPx: 0 });
					return;
				}
				autoScroll(scroller, moveEvent.clientY);
				landing = dropPosAt(moveEvent.clientX, moveEvent.clientY);
				setAnchorDropHint(
					editor.view,
					landing === null ? null : { kind: 'insert', pos: landing }
				);
				// Horizontal travel still feeds the offset: the box keeps the x it is dropped at.
				setDraft({ geometry, liftYPx: moveEvent.clientY - startY });
			};

			const onEnd = (endEvent: PointerEvent): void => {
				window.removeEventListener('pointermove', onMove);
				window.removeEventListener('pointerup', onEnd);
				window.removeEventListener('pointercancel', onEnd);
				setDraft(null);
				setAnchorDropHint(editor.view, null);
				const attributes = geometryAttributes(geometryAt(endEvent.clientX), designWidthMm);
				const pos = getPos();

				if (!relocating || landing === null || typeof pos !== 'number') {
					updateAttributes(attributes);
					return;
				}

				const { view } = editor;
				// Attributes ride on the moved node: a follow-up edit keyed on the old position
				// would write them to whatever took its place.
				const moved = node.type.create({ ...node.attrs, ...attributes });
				const transaction = relocateAnchor(
					view.state,
					{ from: pos, to: pos + node.nodeSize },
					landing,
					moved
				);
				if (transaction === null) {
					updateAttributes(attributes);
					return;
				}
				view.dispatch(transaction);
			};

			// Listeners on window, not the box: the lifted box has `pointer-events: none`.
			window.addEventListener('pointermove', onMove);
			window.addEventListener('pointerup', onEnd);
			window.addEventListener('pointercancel', onEnd);
		};

		return (
			<NodeViewWrapper
				as="div"
				data-date-slot=""
				data-placement-id={placementId}
				data-testid={`date-anchor-${placementId}`}
			>
				<Band ref={bandRef} $heightMm={heightMm} data-testid="date-anchor-band">
					<Box
						ref={boxRef}
						$widthMm={widthMm}
						$offsetXMm={offsetXMm}
						$selected={selected}
						$editable={editor.isEditable}
						$liftYPx={draft?.liftYPx ?? 0}
						data-testid="date-anchor-box"
						onPointerDown={(event) => {
							const from = event.target;
							if (
								from instanceof HTMLElement &&
								from.closest('[data-resize-handle]') !== null
							) {
								return;
							}
							beginGesture(event, 'box');
						}}
					>
						<StampPreview
							slotLabel=""
							isWitness={false}
							roleType={DocumentSigningRoleType.PARTY}
							kind="date"
							showMetadata={showMetadata}
							scale={Math.min(2, Math.max(0.3, widthMm / designWidthMm))}
						/>
						{editor.isEditable && (
							<ResizeGrip
								data-resize-handle
								contentEditable={false}
								aria-label="Resize this date field"
								data-testid="date-anchor-resize"
								onPointerDown={(event) => beginGesture(event, 'resize')}
							/>
						)}
					</Box>
				</Band>
			</NodeViewWrapper>
		);
	};

const Band = styled.div<{ $heightMm: number }>`
	position: relative;
	width: 100%;
	height: ${({ $heightMm }) => $heightMm}mm;
`;

const Box = styled.div<{
	$widthMm: number;
	$offsetXMm: number;
	$selected: boolean;
	$editable: boolean;
	$liftYPx: number;
}>`
	position: absolute;
	top: 0;
	left: ${({ $offsetXMm }) => $offsetXMm}mm;
	width: ${({ $widthMm }) => $widthMm}mm;
	height: 100%;
	max-width: 100%;
	border-radius: 4px;
	cursor: ${({ $editable }) => ($editable ? 'grab' : 'default')};
	touch-action: none;

	/* pointer-events: none while lifted, or every hit test finds the dragged box and the drop is
	   refused as a self-drop. */
	${({ $liftYPx }) =>
		$liftYPx === 0
			? ''
			: `transform: translateY(${$liftYPx}px); opacity: 0.75; z-index: 3;
			   pointer-events: none; cursor: grabbing;`}
	// A shadow, not a border: a border would add to the box and make the anchor occupy more
	// room when selected than the renderer reserves for it.
	box-shadow: ${({ $selected }) =>
		$selected ? `0 0 0 2px ${PALETTE.brand}, 0 0 0 4px rgba(13, 94, 242, 0.25)` : 'none'};

	& > * {
		width: 100%;
		height: 100%;
	}

	&:hover [data-resize-handle] {
		opacity: 1;
	}
`;

const ResizeGrip = styled.span`
	position: absolute;
	right: -6px;
	bottom: -6px;
	z-index: 2;
	width: 12px;
	height: 12px;
	border: 2px solid ${PALETTE.brand};
	border-radius: 2px;
	background: #ffffff;
	cursor: nwse-resize;
	opacity: 0;
	touch-action: none;
	transition: opacity 120ms ease;
`;
